import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ActivityEntry {
  id: string;
  timestamp: Date;
  action: string;
  details: string;
  type: 'create' | 'update' | 'delete' | 'execute' | 'save';
}

export function ActivityLog() {
  const [activities, setActivities] = useState<ActivityEntry[]>([]);

  // Load activities from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('activity-log');
    if (stored) {
      try {
        setActivities(JSON.parse(stored));
      } catch {
        console.warn('Failed to load activity log');
      }
    }
  }, []);

  // Save activities to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('activity-log', JSON.stringify(activities.slice(0, 100))); // Keep last 100
  }, [activities]);

  const addActivity = (action: string, details: string, type: ActivityEntry['type']) => {
    const newActivity: ActivityEntry = {
      id: Math.random().toString(36).substr(2, 9),
      timestamp: new Date(),
      action,
      details,
      type,
    };
    setActivities((prev) => [newActivity, ...prev].slice(0, 100));
  };

  const getTypeColor = (type: ActivityEntry['type']) => {
    switch (type) {
      case 'create':
        return 'bg-green-500/10 text-green-700 border-green-500/20';
      case 'update':
        return 'bg-blue-500/10 text-blue-700 border-blue-500/20';
      case 'delete':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      case 'execute':
        return 'bg-purple-500/10 text-purple-700 border-purple-500/20';
      case 'save':
        return 'bg-amber-500/10 text-amber-700 border-amber-500/20';
      default:
        return 'bg-muted';
    }
  };

  const getTypeLabel = (type: ActivityEntry['type']) => {
    switch (type) {
      case 'create':
        return 'Created';
      case 'update':
        return 'Updated';
      case 'delete':
        return 'Deleted';
      case 'execute':
        return 'Executed';
      case 'save':
        return 'Saved';
      default:
        return 'Unknown';
    }
  };

  const handleClearLog = () => {
    if (confirm('Clear all activity history?')) {
      setActivities([]);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Activity Log</h3>
          <p className="text-sm text-muted-foreground">Track your workflow actions</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearLog}
          disabled={activities.length === 0}
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Clear
        </Button>
      </div>

      <Card className="overflow-hidden">
        <ScrollArea className="h-96">
          {activities.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              <p className="text-sm">No activities yet</p>
            </div>
          ) : (
            <div className="space-y-2 p-4">
              {activities.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-start gap-3 p-3 rounded border border-border hover:bg-muted/50 transition-colors"
                >
                  <Badge className={`flex-shrink-0 ${getTypeColor(activity.type)}`}>
                    {getTypeLabel(activity.type)}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-foreground">{activity.action}</p>
                    <p className="text-xs text-muted-foreground mt-1">{activity.details}</p>
                    <p className="text-xs text-muted-foreground mt-1 opacity-75">
                      {new Date(activity.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </Card>

      <Card className="p-4 bg-blue-500/5 border-blue-500/20">
        <h4 className="font-semibold text-foreground text-sm mb-2">About Activity Log</h4>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li>• Tracks all workflow modifications and executions</li>
          <li>• Stores up to 100 recent activities</li>
          <li>• Automatically saved to browser storage</li>
          <li>• Use for auditing and understanding workflow changes</li>
        </ul>
      </Card>
    </div>
  );
}

export { ActivityLog as default };
export function useActivityLog() {
  const addActivity = (action: string, details: string, type: ActivityEntry['type']) => {
    const stored = localStorage.getItem('activity-log');
    let activities: ActivityEntry[] = [];
    if (stored) {
      try {
        activities = JSON.parse(stored);
      } catch {
        console.warn('Failed to load activity log');
      }
    }

    const newActivity: ActivityEntry = {
      id: Math.random().toString(36).substr(2, 9),
      timestamp: new Date(),
      action,
      details,
      type,
    };

    activities = [newActivity, ...activities].slice(0, 100);
    localStorage.setItem('activity-log', JSON.stringify(activities));
  };

  return { addActivity };
}
